import { __awaiter, __rest } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { ConciergeHeaderWidget } from '@brandclub/bc-widgets';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { toast, Toaster } from 'sonner';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { getHeadersWithUniqueVisitorId } from '../Auth/index.js';
import { fetchProductByStacklineSku } from './api.js';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { PinnedProductContext } from './components/PinnedProductContext.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useConciergeTracking } from './hooks/useConciergeTracking.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { usePageContext } from './hooks/usePageContext.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useConciergePendingQuery } from './stores/useConciergePendingQuery.js';
import { wrapSearchWithProductContext } from './utils/wrapSearchWithProductContext.js';

const ConciergeWidgetChatContent = ({ pageContext: providedPageContext, widgetState, showMaximize = true, isMaximized = false, onMaximize: _onMaximize, onMinimize, onVariantSelect, title, initialQuery, initialQueryStacklineSku, onInitialQueryProcessed, brandId: propBrandId, conciergePageUrl: propConciergePageUrl, }) => {
    var _a, _b;
    // Get conversation state from context (includes apiBaseUrl for checkout-shop-app)
    const conversationState = useConciergeContext();
    // Brand config
    const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
    const params = new URLSearchParams(window.location.search);
    // Priority: prop > URL param > brand config
    const brandId = (propBrandId === null || propBrandId === void 0 ? void 0 : propBrandId.toString()) || params.get('brandId') || ((_b = (_a = config === null || config === void 0 ? void 0 : config.brandIds) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.toString()) || null;
    const appConfigId = params.get('appConfigId') || (config === null || config === void 0 ? void 0 : config.appConfigId) || null;
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // Build concierge page URL with optional brandId and conversationId
    const getConciergePageUrl = useCallback((opts = {}) => {
        const base = propConciergePageUrl !== null && propConciergePageUrl !== void 0 ? propConciergePageUrl : `${window.location.origin}/concierge`;
        const url = new URL(base);
        if (brandId)
            url.searchParams.set('brandId', brandId);
        if (opts.conversationId)
            url.searchParams.set('conversationId', opts.conversationId);
        return url.toString();
    }, [propConciergePageUrl, brandId]);
    // Handle history icon click: open dedicated concierge page in a new window
    const handleHistoryClick = useCallback(() => {
        window.open(getConciergePageUrl(), '_blank', 'noopener,noreferrer');
    }, [getConciergePageUrl]);
    // Handle expand/maximize: open current conversation on concierge page in a new window (instead of expanding popup)
    const handleMaximizeClick = useCallback(() => {
        const url = getConciergePageUrl({ conversationId: conversationState.conversationId });
        window.open(url, '_blank', 'noopener,noreferrer');
    }, [getConciergePageUrl, conversationState.conversationId]);
    // Ref to hold cancelStream so resetConversationState can use it without ordering issues
    const cancelStreamRef = useRef(() => { });
    const resetConversationState = useCallback(() => {
        // Cancel any in-flight stream before clearing state
        cancelStreamRef.current();
        conversationState.setIsActivelyStreaming(false);
        conversationState.clearStreamingState();
        conversationState.setConversationId(null);
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap(new Map());
        conversationState.setFollowUpQuestions([]);
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts([]);
        conversationState.setConversationPageContext(null);
    }, [conversationState]);
    // Get page context - use provided one or fallback to hook
    // usePageContext will work even without react-router (uses window.location)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hookPageContext = usePageContext();
    const pageContext = providedPageContext !== null && providedPageContext !== void 0 ? providedPageContext : hookPageContext;
    // Track previous page context to detect navigation and reset chat
    const previousPageContextRef = useRef(null);
    // Helper: are two page contexts meaningfully different? (same logic for "should reset")
    const isPageContextDifferent = useCallback((a, b) => {
        if (a.pageType !== b.pageType)
            return true;
        if (a.pageType === 'pdp' && b.pageType === 'pdp' && a.stacklineSku !== b.stacklineSku)
            return true;
        return false;
    }, []);
    // Remember where the current conversation was started (so we can reset when opening on a different page)
    useEffect(() => {
        const hasConversation = conversationState.conversationId != null || conversationState.conversationHistory.length > 0;
        if (hasConversation && conversationState.conversationPageContext == null) {
            conversationState.setConversationPageContext(pageContext);
        }
    }, [
        pageContext,
        conversationState.conversationId,
        conversationState.conversationHistory.length,
        conversationState.conversationPageContext,
        conversationState.setConversationPageContext,
    ]);
    // Reset conversation when page context changes (e.g., navigating between products)
    // Also reset on initial mount when opening the widget on a different page than where the conversation was started
    useEffect(() => {
        const previousContext = previousPageContextRef.current;
        const storedConversationContext = conversationState.conversationPageContext;
        if (previousContext === null) {
            // Initial mount (e.g. user closed widget, navigated, then opened again)
            previousPageContextRef.current = pageContext;
            const hasConversation = conversationState.conversationId != null || conversationState.conversationHistory.length > 0;
            if (hasConversation &&
                storedConversationContext != null &&
                isPageContextDifferent(pageContext, storedConversationContext)) {
                resetConversationState();
            }
            return;
        }
        // Detect if the page context has meaningfully changed while widget was open
        const hasPageContextChanged = isPageContextDifferent(previousContext, pageContext);
        if (hasPageContextChanged) {
            resetConversationState();
        }
        // Update the previous context ref
        previousPageContextRef.current = pageContext;
    }, [pageContext, conversationState, isPageContextDifferent, resetConversationState]);
    // Pending access point ref - stores the access point for the next message
    // This allows us to track where a conversation was started from
    const pendingAccessPointRef = useRef(null);
    // Track if this is a new conversation (no existing messages)
    const isNewConversationRef = useRef(true);
    useEffect(() => {
        isNewConversationRef.current = conversationState.conversationHistory.length === 0;
    }, [conversationState.conversationHistory.length]);
    // Initialize tracking hook
    const { trackConversationStart, trackPageContext } = useConciergeTracking({
        conversationId: conversationState.conversationId,
        pageContext,
    });
    // Track when a new conversationId is received (conversation started)
    const previousConversationIdRef = useRef(null);
    useEffect(() => {
        const currentId = conversationState.conversationId;
        const previousId = previousConversationIdRef.current;
        // If we have a new conversation ID and a pending access point, track it
        if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
            const { accessPoint, query } = pendingAccessPointRef.current;
            const isNew = previousId === null;
            // Track the conversation access point (non-blocking)
            trackConversationStart(accessPoint, query, isNew);
            // Clear the pending access point
            pendingAccessPointRef.current = null;
        }
        previousConversationIdRef.current = currentId;
    }, [conversationState.conversationId, trackConversationStart]);
    // Callback to track access point - called by child components
    const handleTrackAccessPoint = useCallback((accessPoint, query) => {
        // Store the access point to be tracked when we get a conversation ID
        pendingAccessPointRef.current = { accessPoint, query };
        // If we already have a conversation ID, track immediately
        if (conversationState.conversationId) {
            const isNew = isNewConversationRef.current;
            trackConversationStart(accessPoint, query, isNew);
            pendingAccessPointRef.current = null;
        }
    }, [conversationState.conversationId, trackConversationStart]);
    // Get widget state and pending query
    const { isOpen } = widgetState;
    const { pendingQuery, pendingStacklineSku, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
    // Ref to track the last processed query ID to prevent duplicate sends
    const lastProcessedQueryIdRef = useRef(null);
    // Auto-scroll behavior
    const { chatWrapperRef, messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Handle stream errors - restore input and show appropriate toast
    const handleStreamError = useCallback((errorType, originalMessage) => {
        setSearchInputValue(originalMessage);
        const errorMessages = {
            rate_limit: 'Too many requests. Please wait a moment before trying again.',
            network: 'Network error. Please check your connection and try again.',
            server: 'Something went wrong on our end. Please try again.',
            unknown: 'Something went wrong. Please try again.',
        };
        toast.error(errorMessages[errorType]);
    }, []);
    // Streaming search - expose performSearch for direct calls from pending query handler
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationState,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        onStreamError: handleStreamError,
    });
    // Keep the ref in sync so resetConversationState can use it
    cancelStreamRef.current = cancelStream;
    // Handle search submission
    const handleSearch = useCallback((searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        // Only wrap with product context if this is the FIRST message in the conversation
        // This avoids repeating the product info for every message
        const isFirstMessage = conversationState.conversationHistory.length === 0;
        const searchTermWithContext = isFirstMessage ? wrapSearchWithProductContext(searchTerm, pageContext) : searchTerm;
        yield performSearch(searchTermWithContext, imageData, imageMimeType);
    }), [pageContext, performSearch, conversationState.conversationHistory.length]);
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Conversation loader
    const { isLoadingConversationQuery } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl: conversationState.apiBaseUrl,
        onConversationLoaded: (messages, productMap, surveyMap, followUpQuestions, previewSearchQueries, previewQueryProducts, sharingInfo) => {
            var _a;
            conversationState.setProductMetadataMap(productMap);
            conversationState.setSurveyMetadataMap(surveyMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
            // Propagate sharing metadata into context state
            if (sharingInfo) {
                conversationState.setIsSharedView((_a = sharingInfo.isSharedView) !== null && _a !== void 0 ? _a : false);
                conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
                conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
                conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
            }
            else {
                conversationState.setIsSharedView(false);
                conversationState.setSharedByUserName(undefined);
                conversationState.setSourceConversationId(undefined);
                conversationState.setSourceCheckpointIndex(undefined);
            }
        },
        onConversationNotFound: () => {
            conversationState.setConversationId(null);
            conversationState.setConversationPageContext(null);
        },
    });
    // Sync conversationId with URL (disabled in widget)
    // useUrlSync(conversationState.conversationId);
    // Auto-send from URL params (disabled in widget)
    // useAutoSendFromUrl({
    //   isActivelyStreaming: conversationState.isActivelyStreaming,
    //   conversationLength: conversationState.conversationHistory.length,
    //   onSearch: handleSearch,
    // });
    // Handle pending queries from PDP conversation starters (Zustand-based - for backward compatibility)
    // This effect runs when: widget opens, or a new pending query is set
    useEffect(() => {
        // Skip if no pending query or widget not open
        if (!pendingQuery || !pendingQueryId || !isOpen) {
            return;
        }
        // Guard: Ensure performSearch is ready before processing
        if (!performSearch || typeof performSearch !== 'function') {
            return;
        }
        // Skip if we already processed this exact query (by ID)
        if (lastProcessedQueryIdRef.current === pendingQueryId) {
            return;
        }
        // Mark as processed immediately to prevent re-processing
        lastProcessedQueryIdRef.current = pendingQueryId;
        // Capture values before clearing
        const queryToSend = pendingQuery;
        const stacklineSkuToUse = pendingStacklineSku;
        // Clear the pending query immediately
        clearPendingQuery();
        // Only wrap with product context if this is the FIRST message in the conversation
        // This avoids repeating the product info for every message
        const isFirstMessage = conversationState.conversationHistory.length === 0;
        let messageToSend = queryToSend;
        if (isFirstMessage && stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Add a small delay to ensure the component is fully mounted and ready
        // This prevents crashes when the widget opens and immediately tries to process a query
        const timeoutId = setTimeout(() => {
            // Double-check that widget is still open and performSearch is still available
            if (!isOpen || !performSearch || typeof performSearch !== 'function') {
                // Reset the processed query ID so it can be retried later
                lastProcessedQueryIdRef.current = null;
                return;
            }
            // Send the message - performSearch handles adding user message and streaming response
            // Wrap in try-catch to prevent crashes if performSearch fails
            try {
                // Track conversation_starter access point (non-blocking)
                // This tracks PDP conversation starters (e.g., "Ask about this product" buttons)
                handleTrackAccessPoint('conversation_starter', queryToSend);
                performSearch(messageToSend);
            }
            catch (error) {
                console.error('Error processing pending query:', error);
                toast.error('Failed to process search. Please try again.');
                // Reset the processed query ID so it can be retried
                lastProcessedQueryIdRef.current = null;
            }
        }, 100); // Small delay to ensure component is fully mounted
        // Cleanup timeout if component unmounts or dependencies change
        return () => {
            clearTimeout(timeoutId);
        };
    }, [
        isOpen,
        pendingQuery,
        pendingQueryId,
        pendingStacklineSku,
        performSearch,
        clearPendingQuery,
        handleTrackAccessPoint,
    ]);
    // Handle initial query from props (props-based - for checkout-shop-app and PDP conversation starters)
    // This avoids cross-bundle Zustand store issues by using props instead
    const lastProcessedInitialQueryRef = useRef(null);
    const isProcessingInitialQueryRef = useRef(false);
    useEffect(() => {
        // Skip if no initialQuery or widget not open
        if (!initialQuery || !isOpen) {
            return;
        }
        // Guard: Ensure performSearch is ready before processing
        if (!performSearch || typeof performSearch !== 'function') {
            return;
        }
        // Skip if we already processed this exact query
        if (lastProcessedInitialQueryRef.current === initialQuery) {
            return;
        }
        // Skip if we're currently processing (timeout is pending)
        if (isProcessingInitialQueryRef.current) {
            return;
        }
        // Mark that we're starting to process (prevents duplicate timeouts)
        isProcessingInitialQueryRef.current = true;
        // Capture values before callback
        const queryToSend = initialQuery;
        const stacklineSkuToUse = initialQueryStacklineSku;
        // Only wrap with product context if this is the FIRST message in the conversation
        // This avoids repeating the product info for every message
        const isFirstMessage = conversationState.conversationHistory.length === 0;
        let messageToSend = queryToSend;
        if (isFirstMessage && stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Add a small delay to ensure the component is fully mounted and ready
        const timeoutId = setTimeout(() => {
            // Double-check that widget is still open and performSearch is still available
            if (!isOpen || !performSearch || typeof performSearch !== 'function') {
                // Reset so it can be retried later
                isProcessingInitialQueryRef.current = false;
                return;
            }
            // Send the message
            try {
                // Track conversation_starter access point for PDP pills
                handleTrackAccessPoint('conversation_starter', queryToSend);
                performSearch(messageToSend);
                // Mark as successfully processed (prevents re-processing same query)
                lastProcessedInitialQueryRef.current = queryToSend;
                // Notify parent that the query has been processed
                onInitialQueryProcessed === null || onInitialQueryProcessed === void 0 ? void 0 : onInitialQueryProcessed();
            }
            catch (error) {
                console.error('Error processing initial query:', error);
                toast.error('Failed to process search. Please try again.');
            }
            finally {
                isProcessingInitialQueryRef.current = false;
            }
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            // Don't reset isProcessingInitialQueryRef here - let the effect re-run and decide
            // based on whether the query has actually been processed
            isProcessingInitialQueryRef.current = false;
        };
    }, [isOpen, initialQuery, initialQueryStacklineSku, performSearch, onInitialQueryProcessed, handleTrackAccessPoint]);
    // State for fetched pinned product
    // NOTE: These hooks MUST be called before any conditional returns to follow React's rules of hooks
    const [pinnedProduct, setPinnedProduct] = useState(null);
    const [isPinnedProductLoading, setIsPinnedProductLoading] = useState(false);
    // Fetch pinned product when page context changes
    useEffect(() => {
        // Derive stacklineSku inside the effect to avoid stale closures
        const currentPageType = pageContext === null || pageContext === void 0 ? void 0 : pageContext.pageType;
        const currentStacklineSku = currentPageType === 'pdp' ? pageContext === null || pageContext === void 0 ? void 0 : pageContext.stacklineSku : undefined;
        // Clear previous product when context changes
        setPinnedProduct(null);
        setIsPinnedProductLoading(false);
        if (!currentStacklineSku) {
            return;
        }
        // First check if product is already in the metadata map
        const existingProduct = conversationState.productMetadataMap.get(currentStacklineSku);
        if (existingProduct) {
            setPinnedProduct(existingProduct);
            return;
        }
        // Fetch product from server
        let isCancelled = false;
        const fetchProduct = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsPinnedProductLoading(true);
            try {
                const headers = yield getHeadersWithUniqueVisitorId();
                const product = yield fetchProductByStacklineSku({
                    stacklineSku: currentStacklineSku,
                    headersToInject: headers,
                    apiBaseUrl: conversationState.apiBaseUrl,
                });
                if (!isCancelled && product) {
                    setPinnedProduct(product);
                    // Also add to the metadata map for future use
                    conversationState.setProductMetadataMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(currentStacklineSku, product);
                        return newMap;
                    });
                }
            }
            catch (error) {
                if (!isCancelled) {
                    console.error('Error fetching pinned product:', error);
                }
            }
            finally {
                if (!isCancelled) {
                    setIsPinnedProductLoading(false);
                }
            }
        });
        fetchProduct();
        return () => {
            isCancelled = true;
        };
    }, [pageContext === null || pageContext === void 0 ? void 0 : pageContext.pageType, pageContext === null || pageContext === void 0 ? void 0 : pageContext.stacklineSku, conversationState.apiBaseUrl]);
    // Derive the pinned SKU for filtering
    const pinnedStacklineSku = (pageContext === null || pageContext === void 0 ? void 0 : pageContext.pageType) === 'pdp' ? pageContext.stacklineSku : undefined;
    // Filter out pinned product from streamingFeaturedProduct (already shown in pinned bar)
    const filteredStreamingFeaturedProduct = useMemo(() => {
        if (!conversationState.streamingFeaturedProduct || !pinnedStacklineSku) {
            return conversationState.streamingFeaturedProduct;
        }
        if (conversationState.streamingFeaturedProduct.product.stacklineSku === pinnedStacklineSku) {
            return null;
        }
        return conversationState.streamingFeaturedProduct;
    }, [conversationState.streamingFeaturedProduct, pinnedStacklineSku]);
    // Filter out pinned product from conversation history's featured products and message content
    const filteredConversationHistory = useMemo(() => {
        if (!pinnedStacklineSku) {
            return conversationState.conversationHistory;
        }
        // Regex to match featured product tags with the pinned SKU
        // Matches: <product featured="true" stacklineSku="SKU" question="..." /> or without self-closing
        const pinnedProductTagRegex = new RegExp(`<product\\s+featured\\s*=\\s*"true"\\s+stacklineSku\\s*=\\s*"${pinnedStacklineSku}"(?:\\s+question\\s*=\\s*"[^"]*")?\\s*/?>`, 'g');
        return conversationState.conversationHistory.map((message) => {
            var _a, _b;
            let updatedMessage = message;
            // Remove the featured product prop if it matches pinned SKU
            if ('featuredProduct' in message && ((_b = (_a = message.featuredProduct) === null || _a === void 0 ? void 0 : _a.product) === null || _b === void 0 ? void 0 : _b.stacklineSku) === pinnedStacklineSku) {
                const rest = __rest(message, ["featuredProduct"]);
                updatedMessage = rest;
            }
            // Also strip the featured product tags from message content that match pinned SKU
            if (updatedMessage.content && pinnedProductTagRegex.test(updatedMessage.content)) {
                // Reset regex lastIndex since we're using global flag
                pinnedProductTagRegex.lastIndex = 0;
                const filteredContent = updatedMessage.content.replace(pinnedProductTagRegex, '').trim();
                updatedMessage = Object.assign(Object.assign({}, updatedMessage), { content: filteredContent });
            }
            return updatedMessage;
        });
    }, [conversationState.conversationHistory, pinnedStacklineSku]);
    // Filter out pinned product tags from streaming AI response
    const filteredStreamingAIResponse = useMemo(() => {
        if (!pinnedStacklineSku || !conversationState.streamingAIResponse) {
            return conversationState.streamingAIResponse;
        }
        // Regex to match featured product tags with the pinned SKU
        const pinnedProductTagRegex = new RegExp(`<product\\s+featured\\s*=\\s*"true"\\s+stacklineSku\\s*=\\s*"${pinnedStacklineSku}"(?:\\s+question\\s*=\\s*"[^"]*")?\\s*/?>`, 'g');
        return conversationState.streamingAIResponse.replace(pinnedProductTagRegex, '').trim();
    }, [conversationState.streamingAIResponse, pinnedStacklineSku]);
    return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                } }, { children: [jsx(ConciergeHeaderWidget, { showMaximize: showMaximize && !!conversationState.conversationId, isMaximized: isMaximized, onMaximize: handleMaximizeClick, onMinimize: onMinimize, onClose: widgetState.close, onHistoryClick: handleHistoryClick, title: title }), jsx(PinnedProductContext, { pageContext: pageContext, product: pinnedProduct, isLoading: isPinnedProductLoading }), jsx(ConciergeChatAreaV2, { appName: config === null || config === void 0 ? void 0 : config.appName, brandId: brandId, conversationId: conversationState.conversationId, isEmbedded: true, apiBaseUrl: conversationState.apiBaseUrl, pageContext: pageContext, conversationHistory: filteredConversationHistory, productMetadataMap: conversationState.productMetadataMap, surveyMetadataMap: conversationState.surveyMetadataMap, streamingProductGroups: conversationState.streamingProductGroups, previewSearchQueries: conversationState.previewSearchQueries, previewQueryProducts: conversationState.previewQueryProducts, followUpQuestions: conversationState.followUpQuestions, streamingFeaturedProduct: filteredStreamingFeaturedProduct, streamingStoreLocator: conversationState.streamingStoreLocator, isActivelyStreaming: conversationState.isActivelyStreaming, streamingAIResponse: filteredStreamingAIResponse, currentStatus: conversationState.currentStatus, isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries, isLoadingConversationQuery: isLoadingConversationQuery, handleSearch: handleSearch, onUpdateFeedback: handleUpdateFeedback, onVariantSelect: onVariantSelect, onTrackAccessPoint: handleTrackAccessPoint, searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue, isSharedView: conversationState.isSharedView, sharedByUserName: conversationState.sharedByUserName, sourceConversationId: conversationState.sourceConversationId, sourceCheckpointIndex: conversationState.sourceCheckpointIndex })] }))] }));
};
// ConciergeWidgetChat expects to be inside a QueryClientProvider (provided by ConciergeStandalone)
const ConciergeWidgetChat = (props) => {
    return jsx(ConciergeWidgetChatContent, Object.assign({}, props));
};

export { ConciergeWidgetChat };
//# sourceMappingURL=ConciergeWidgetChat.js.map
